import type { z } from "zod";
import type { ValidatableClass } from "../../schemas/validatableClass.js";
import { VALIDATE_METADATA_KEY } from "../decorators/constants.js";

/**
 * Options for the validate decorator used to define the Zod schema for a field
 *
 * @param schemaKey - The schema key for the class. Required field on the decorator for the 'schemaKey' field.
 * @param zodValidator - A basic Zod validator to use for "primitive" fields like z.string(), z.number(), etc.
 * @param responseOnly - If true, the field will only be applied to the base schema variants. Used to ignore fields for create/update validators.
 * @param childClass - A child schema class whose constructor satisfies {@link ValidatableClass} (via `static dtoValidators`).
 * @param childClassTransform - A function to transform the child class to the correct Zod schema variant. eg. for the type `ChildClass[]` one would pass in `(object) => z.array(object)`
 * @param requiredOnUpdate - Reflects whether the field is required on update (typically all update request body fields are optional). Invalid if combined with an `.optional()` on the zodValidator.
 * @param allowPartialOnUpdate - If true, the child class will use it's corresponding update validator, making all fields optional. No effect if used on a basic zodValidator.
 * @param hasDefault - If true, the field is optional on create request validators but required on response (base/baseLax) validators. Use when the schema class has a Mongoose/TypeScript default and create payloads may omit the property.
 */
export type ValidateOptions = {
    schemaKey?: string;
    zodValidator?: z.ZodType;
    responseOnly?: boolean;
    childClass?: ValidatableClass;
    childClassTransform?: (value: z.ZodType) => z.ZodType;
    requiredOnUpdate?: boolean;
    allowPartialOnUpdate?: boolean;
    hasDefault?: boolean;
};

export function validate(options: ValidateOptions) {
    return (target: object, key: string) => {
        const metadata: Record<string, ValidateOptions> =
            Reflect.getMetadata(VALIDATE_METADATA_KEY, target.constructor) || {};

        Reflect.defineMetadata(VALIDATE_METADATA_KEY, { ...metadata, [key]: options }, target.constructor);
    };
}
